import React from "react";
import { FaCheckCircle, FaArrowRight, FaRing, FaDrumstickBite, FaUtensilSpoon } from "react-icons/fa";
import { SITE_CONTENT } from "../content";

const SpotlightsSection = () => {
  const { wedding, nonVeg, biryani } = SITE_CONTENT.pages.homePage.spotlights;

  const scrollToBooking = (e) => {
    e.preventDefault();
    const el = document.getElementById("booking-section");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="bg-[#fcfaf7] text-[#1a2e1d] divide-y divide-[#ebe4d6]">
      
      {/* ── Spotlight 1: Wedding Catering Service Near You ── */}
      <section className="py-24 lg:py-32 relative overflow-hidden" id="wedding-spotlight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Visual Column */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                <img
                  src={wedding.image}
                  alt={wedding.title}
                  className="w-full h-[450px] lg:h-[520px] object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f12]/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 p-6 rounded-2xl bg-white/95 backdrop-blur-md shadow-lg border border-[#e8a040]/30">
                  <div className="flex items-center gap-2 text-[#b8731b] font-bold text-xs uppercase tracking-wider mb-1">
                    <FaRing />
                    <span>Grand Wedding Catering</span>
                  </div>
                  <p className="text-[#0a1a0f] text-sm font-semibold">
                    Multi-course outdoor banquets & live buffet management
                  </p>
                </div>
              </div>
            </div>

            {/* Content Column */}
            <div className="lg:col-span-7 order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#E8A040]/15 text-[#b8731b] text-xs font-bold tracking-widest uppercase mb-4 border border-[#E8A040]/30">
                <FaRing className="w-3 h-3" />
                Grand Occasions
              </span>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light font-serif leading-tight text-[#0a1a0f] mb-6">
                {wedding.title}
              </h2>

              <p className="text-[#4e5a52] text-base sm:text-lg leading-relaxed font-light mb-8">
                {wedding.intro}
              </p>

              {/* 4 Wedding Pillars */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
                {wedding.pillars.map((pillar, i) => (
                  <div key={i} className="p-5 rounded-2xl bg-white border border-[#e3dcd1] shadow-sm hover:border-[#E8A040] transition-colors">
                    <div className="w-8 h-8 rounded-full bg-[#0a1f12] text-[#E8A040] flex items-center justify-center text-xs font-bold mb-3 shadow">
                      0{i + 1}
                    </div>
                    <h3 className="font-semibold text-base text-[#0a1a0f] mb-2">{pillar.title}</h3>
                    <p className="text-sm text-[#5a665e] leading-relaxed font-light">{pillar.description}</p>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <a
                href="#booking-section"
                onClick={scrollToBooking}
                className="inline-flex items-center gap-3 px-8 py-4 bg-[#0a1f12] text-white hover:bg-[#13351f] rounded-full text-xs font-bold tracking-[0.15em] uppercase transition-all duration-300 hover:scale-105 shadow-xl group"
              >
                <span>{wedding.ctaText}</span>
                <FaArrowRight className="text-[#E8A040] w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
              </a>
            </div>

          </div>
        </div>
      </section>

      {/* ── Spotlight 2: Non-Veg Catering Services Near You ── */}
      <section className="py-24 lg:py-32 relative bg-[#f5f1e8] overflow-hidden" id="non-veg-spotlight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Content Column */}
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1b4329]/15 text-[#1b4329] text-xs font-bold tracking-widest uppercase mb-4 border border-[#1b4329]/30">
                <FaDrumstickBite className="w-3 h-3" />
                Our Core Specialty
              </span>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light font-serif leading-tight text-[#0a1a0f] mb-6">
                {nonVeg.title}
              </h2>

              <p className="text-[#4e5a52] text-base sm:text-lg leading-relaxed font-light mb-6">
                {nonVeg.intro}
              </p>

              {/* 5 Core Items */}
              <div className="space-y-3 mb-8">
                {nonVeg.items.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3.5 rounded-xl bg-white border border-[#e5ddd0] shadow-sm">
                    <FaCheckCircle className="text-[#b8731b] w-4 h-4 shrink-0" />
                    <span className="text-[#203025] text-sm sm:text-base font-medium">{item}</span>
                  </div>
                ))}
              </div>

              {/* Additional Context Note */}
              <p className="text-[#536056] text-sm sm:text-[15px] leading-relaxed mb-8 bg-white/60 p-5 rounded-2xl border border-[#e5ddd0] italic">
                {nonVeg.note}
              </p>

              {/* CTA */}
              <a
                href="#booking-section"
                onClick={scrollToBooking}
                className="inline-flex items-center gap-3 px-8 py-4 bg-[#b8731b] text-white hover:bg-[#9c5f13] rounded-full text-xs font-bold tracking-[0.15em] uppercase transition-all duration-300 hover:scale-105 shadow-xl group"
              >
                <span>{nonVeg.ctaText}</span>
                <FaArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
              </a>
            </div>

            {/* Visual Column */}
            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                <img
                  src={nonVeg.image}
                  alt={nonVeg.title}
                  className="w-full h-[450px] lg:h-[520px] object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f12]/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 p-6 rounded-2xl bg-[#0a1a0f]/90 text-white backdrop-blur-md border border-[#E8A040]/30 shadow-xl">
                  <span className="text-[#22c55e] text-xs font-bold tracking-widest uppercase block mb-1">
                    100% Halal Certified
                  </span>
                  <p className="text-sm font-light text-white/90">
                    Daily farm-fresh meats, zero frozen ingredients, cooked live on-site.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Spotlight 3: Biryani Catering Near You ── */}
      <section className="py-24 lg:py-32 relative overflow-hidden" id="biryani-spotlight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Visual Column */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                <img
                  src={biryani.image}
                  alt={biryani.title}
                  className="w-full h-[450px] lg:h-[520px] object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f12]/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 p-6 rounded-2xl bg-white/95 backdrop-blur-md shadow-lg border border-[#e8a040]/30">
                  <span className="text-[#b8731b] text-xs font-bold tracking-widest uppercase block mb-1">
                    Event-Sized Woodfire Handi
                  </span>
                  <p className="text-[#0a1a0f] text-sm font-semibold">
                    Slow-dum cooking preserves fragrance, spice depth & tender cuts.
                  </p>
                </div>
              </div>
            </div>

            {/* Content Column */}
            <div className="lg:col-span-7 order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#E8A040]/15 text-[#b8731b] text-xs font-bold tracking-widest uppercase mb-4 border border-[#E8A040]/30">
                <FaUtensilSpoon className="w-3 h-3" />
                Crowd Favourite
              </span>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light font-serif leading-tight text-[#0a1a0f] mb-6">
                {biryani.title}
              </h2>

              <p className="text-[#4e5a52] text-base sm:text-lg leading-relaxed font-light mb-6">
                {biryani.intro}
              </p>

              {/* 5 Event Occasions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {biryani.occasions.map((occasion, i) => (
                  <div key={i} className="flex items-center gap-2.5 p-3.5 rounded-xl bg-white border border-[#e5ddd0] shadow-sm">
                    <span className="w-2 h-2 rounded-full bg-[#b8731b] shrink-0" />
                    <span className="text-[#203025] text-sm font-medium leading-snug">{occasion}</span>
                  </div>
                ))}
              </div>

              {/* Golden Rule Note */}
              <div className="p-6 rounded-2xl bg-[#092011] text-white border border-[#1b4329] mb-8 shadow-md">
                <h4 className="text-[#E8A040] text-sm font-bold uppercase tracking-wider mb-2">
                  The Three Golden Rules
                </h4>
                <p className="text-white/85 text-sm sm:text-[15px] leading-relaxed font-light">
                  {biryani.note}
                </p>
              </div>

              {/* CTA */}
              <a
                href="#booking-section"
                onClick={scrollToBooking}
                className="inline-flex items-center gap-3 px-8 py-4 bg-[#0a1f12] text-white hover:bg-[#13351f] rounded-full text-xs font-bold tracking-[0.15em] uppercase transition-all duration-300 hover:scale-105 shadow-xl group"
              >
                <span>{biryani.ctaText}</span>
                <FaArrowRight className="text-[#E8A040] w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
              </a>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default SpotlightsSection;

